import { useCallback, useRef, useState } from "react";

import { updateFavorite } from "@/api/favorites";
import type { Item } from "@/api/items";
import { toast } from "@/components/Toast";
import { ActionBar } from "./ActionBar";
import { ContentRenderer } from "./ContentRenderer";

const MSG_TYPE_LABEL: Record<string, string> = {
  text: "文本",
  post: "富文本",
  image: "图片",
  file: "文件",
  sticker: "表情",
  audio: "语音",
  video: "视频",
  interactive: "卡片",
};

function formatTime(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("zh-CN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString("zh-CN", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
}

function defaultTitle(contentText: string): string {
  const t = contentText.trim();
  return t.length > 30 ? t.slice(0, 30) + "…" : t || "无标题";
}

export function FavoriteCard({
  item,
  onDeleted,
  onPinChanged,
  onTitleChanged,
  highlight,
  innerRef,
}: {
  item: Item;
  onDeleted?: () => void;
  onPinChanged?: () => void;
  onTitleChanged?: () => void;
  highlight?: boolean;
  innerRef?: (el: HTMLElement | null) => void;
}) {
  const isPinned = !!item.pinned_at;
  const typeLabel = MSG_TYPE_LABEL[item.msg_type] ?? item.msg_type;
  const isOwn = item.kind === "own";

  // ---- title editing ----
  const [editing, setEditing] = useState(false);
  const [titleValue, setTitleValue] = useState(item.title ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback(() => {
    setTitleValue(item.title ?? "");
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [item.title]);

  const commitTitle = useCallback(async () => {
    if (saving) return;
    setEditing(false);
    const newTitle = titleValue.trim() || null;
    if (newTitle === (item.title ?? null)) return; // unchanged
    setSaving(true);
    try {
      await updateFavorite(item.favorite_id, { title: newTitle });
      onTitleChanged?.();
    } catch {
      toast.error("标题保存失败");
    } finally {
      setSaving(false);
    }
  }, [titleValue, item.title, item.favorite_id, saving, onTitleChanged]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setTitleValue(item.title ?? "");
  }, [item.title]);

  // ---- content expand/collapse ----
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    // Check after render if content exceeds max height
    requestAnimationFrame(() => {
      setHasOverflow(el.scrollHeight > el.clientHeight + 2);
    });
  }, []);

  return (
    <article
      ref={innerRef}
      className={`group rounded-xl border bg-white p-4 shadow-sm transition hover:shadow-md ${
        highlight
          ? "border-sky-300 ring-2 ring-sky-200"
          : isPinned
            ? "border-amber-200 bg-amber-50/40"
            : "border-slate-200"
      }`}
    >
      {item.kind === "shared" ? (
        <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-700">
          <span>↗</span>
          <span>
            来自{" "}
            <strong className="font-semibold">{item.shared_by_name ?? "某人"}</strong>
            {item.via_group_id ? " · 经由分组" : ""}
          </span>
        </div>
      ) : null}

      {/* Title row */}
      <div className="mb-2 flex items-center gap-2">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitTitle();
              if (e.key === "Escape") cancelEdit();
            }}
            maxLength={200}
            placeholder="输入标题（留空则自动）"
            className="flex-1 rounded border border-sky-300 px-2 py-0.5 text-sm text-slate-800 outline-none focus:ring-1 focus:ring-sky-400"
          />
        ) : (
          <h3
            className="flex-1 truncate text-sm font-medium text-slate-800"
            title={item.title ?? defaultTitle(item.content_text)}
          >
            {item.title ?? defaultTitle(item.content_text)}
          </h3>
        )}
        {isOwn && !editing ? (
          <button
            onClick={startEdit}
            title="编辑标题"
            className="shrink-0 rounded p-0.5 text-slate-400 opacity-0 transition hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100"
          >
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793z" />
              <path d="M11.379 5.793L3 14.172V17h2.828l8.38-8.38-2.83-2.828z" />
            </svg>
          </button>
        ) : null}
      </div>

      {/* Metadata row */}
      <div className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-500">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-slate-700">
            {item.sender_name ?? "未知发送者"}
          </span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
            {typeLabel}
          </span>
          {isPinned ? (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
              📌 置顶
            </span>
          ) : null}
        </div>
        <time className="shrink-0 tabular-nums">
          {formatTime(item.original_create_time ?? item.list_time)}
        </time>
      </div>

      {/* Content with fixed height */}
      <div
        ref={(el) => {
          (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          checkOverflow(el);
        }}
        className={`relative overflow-hidden transition-[max-height] duration-300 ${
          expanded ? "max-h-[2000px]" : "max-h-40"
        }`}
      >
        <ContentRenderer item={item} />
        {!expanded && hasOverflow ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
        ) : null}
      </div>
      {hasOverflow ? (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 w-full text-center text-xs text-sky-600 hover:text-sky-800"
        >
          {expanded ? "收起" : "展开全文"}
        </button>
      ) : null}

      <ActionBar item={item} onDeleted={onDeleted} onPinChanged={onPinChanged} />
    </article>
  );
}
